import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChange } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { debounceTime } from 'rxjs/operators';
import english from '../../../../main/maintenances/i18n/en.json';
import spanish from '../../../../main/maintenances/i18n/es.json';
import portuguese from '../../../../main/maintenances/i18n/pt.json';
import { LocationsApi } from '../../../api/locations.api';
import { Location, PagedResult } from './../../../interfaces';

export function ValidateAutocomplete(): object {
  return { required: true };
}

const INPUT_FIELD_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LocationsComponent),
  multi: true
};

@Component({
  selector: 'locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class LocationsComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() hint: boolean;
  @Input() floatLabel: boolean;
  @Input() appearance = 'outline';
  public locations: Array<Location> = [];
  public inputValue = new FormControl();
  public selectedLocation: Location;
  public loading = false;

  constructor(private fuseTranslationLoaderService: FuseTranslationLoaderService, private locationsApi: LocationsApi) {
    this.fuseTranslationLoaderService.loadJsonTranslations({english, portuguese, spanish});
    if (this.required) {
      this.inputValue.setValidators(ValidateAutocomplete);
    }
  }

  ngOnInit(): void {
    if (this.disabled) {
      this.inputValue.disable();
    }
    this.inputValue.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.locations = [];
      if (value) {
        this.searchLocations(value);
      }
    });
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }): void {
    for (const propertyName in changes) {
      if (propertyName === 'disabled') {
        this.disabled = changes[propertyName].currentValue;
        if (this.disabled) {
          this.inputValue.disable();
        } else {
          this.inputValue.enable();
        }
      }
    }
  }

  onChange: (_: any) => void = () => { };
  onTouched: (_: any) => void = () => { };

  searchLocations(searchTerm: string): void {
    this.loading = true;
    this.locationsApi
      .search(
        { formattedAddressOrDocumentNumber_contains: searchTerm, archived: false },
        {
          orderLegacy: false,
          orderColumn: 'formattedAddress',
          order: 'asc',
          offset: 0,
          operator: 'distance',
          value: searchTerm,
          limit: 10
        }
      )
      .subscribe((result: PagedResult) => {
        this.loading = false;
        this.locations = result.items;
      });
  }

  writeValue(locationId: any): void {
    if (locationId) {
      this.locationsApi.searchOne(locationId).subscribe((location) => {
        this.selectedLocation = location;
        this.inputValue.setErrors(null);
      });
    } else {
      this.deselectLocation();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setSelectedLocation(): void {
    if (this.inputValue.value) {
      this.selectedLocation = this.locations.find((location) => location.id === this.inputValue.value);
      this.onChange(this.inputValue.value);
      this.inputValue.setErrors(null);
    }
  }

  deselectLocation(): void {
    this.onChange(null);
    this.inputValue.setValue(null);
    this.selectedLocation = null;
    this.inputValue.setErrors(null);
  }
}
